import { createFileRoute, notFound } from "@tanstack/react-router";
import { VillaHome } from "@/components/VillaHome";
import { getPricingSettings } from "@/lib/pricing.functions";
import { HERO, REVIEW_AGGREGATE, T, TESTIMONIALS, isLang, type Lang } from "@/lib/villa-content";

const SITE = "https://www.casamipajobe.com";

const SCHEMA_IMAGES = [
  `${SITE}/__l5e/assets-v1/c2da6d17-8014-438e-ab64-030ffdb19d02/hoofdfoto_orig.jpg`,
  `${SITE}/__l5e/assets-v1/3d980320-8bc8-4938-a85d-2c15d5cd3fb5/20200719-lounge-1-boog-avond_orig.jpg`,
  `${SITE}/__l5e/assets-v1/34083448-d0ae-4ca9-a415-786456c09a5a/20200722-vanuit-hoek-naar-lounge-dag-2_orig.jpg`,
  `${SITE}/__l5e/assets-v1/fec6c96c-b928-4e19-9b4e-5dd9e42d4c17/20200719-lounge-dichtbij-dag_orig.jpg`,
  `${SITE}/__l5e/assets-v1/c11bc4b0-4b5f-4fb5-9f03-c6c98a4959cc/20200719-zicht-op-buitenkeuken-avond-4_orig.jpg`,
  `${SITE}/__l5e/assets-v1/192a8719-51e9-4864-be20-2758486bbafa/20210729-084915-24494_orig.jpg`,
  `${SITE}/__l5e/assets-v1/6b934030-209b-49a3-a0ba-0e337ddd1db2/20200722-tuin-helemaal_orig.jpg`,
  `${SITE}/__l5e/assets-v1/6c9c8352-e1d6-44c9-973c-244acea5db85/20200722-ligzetels-onder-palm_orig.jpg`,
];

const TITLE_BY_LANG: Record<Lang, string> = {
  nl: "Villa met Privézwembad Huren | La Nucia, Costa Blanca – Casa Mi Pajobe",
  en: "Private Pool Villa Rental | La Nucia, Costa Blanca – Casa Mi Pajobe",
  fr: "Villa avec Piscine Privée à Louer | La Nucia, Costa Blanca – Casa Mi Pajobe",
  de: "Villa mit Privatpool Mieten | La Nucia, Costa Blanca – Casa Mi Pajobe",
};

const META_DESC_BY_LANG: Record<Lang, string> = {
  nl: "Vrijstaande vakantievilla met privézwembad en buitenkeuken in La Nucia, 5 km van Benidorm. 4 slaapkamers, tot 8 personen. Boek rechtstreeks, beste prijs.",
  en: "Detached villa with private pool and outdoor kitchen in La Nucia, 5 km from Benidorm. 4 bedrooms, sleeps 8. Book direct for the best price.",
  fr: "Villa indépendante avec piscine privée et cuisine extérieure à La Nucia, à 5 km de Benidorm. 4 chambres, jusqu'à 8 personnes. Réservez en direct.",
  de: "Freistehende Ferienvilla mit Privatpool und Außenküche in La Nucia, 5 km von Benidorm. 4 Schlafzimmer, bis zu 8 Personen. Direkt buchen.",
};

export const HREFLANG_LINKS = [
  { rel: "alternate", hrefLang: "nl", href: `${SITE}/nl` },
  { rel: "alternate", hrefLang: "en", href: `${SITE}/en` },
  { rel: "alternate", hrefLang: "fr", href: `${SITE}/fr` },
  { rel: "alternate", hrefLang: "de", href: `${SITE}/de` },
  { rel: "alternate", hrefLang: "x-default", href: `${SITE}/` },
];

export const Route = createFileRoute("/$lang")({
  beforeLoad: ({ params, location }) => {
    const p = location.pathname;
    if (p.startsWith("/lovable/") || p === "/email/unsubscribe") return;
    if (!isLang(params.lang)) throw notFound();
    return { lang: params.lang as Lang };
  },
  // Load pricing on the server so SSR and hydration render identical rates.
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["pricing-settings"],
      queryFn: () => getPricingSettings(),
      staleTime: 5 * 60_000,
    });
  },
  head: ({ params }) => {
    const lang = isLang(params.lang) ? (params.lang as Lang) : "en";
    const t = T[lang];
    const reviews = TESTIMONIALS.map((r) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5", worstRating: "1" },
      author: { "@type": "Person", name: r.name },
      datePublished: `${r.date}-01`,
      reviewBody: r.quote[lang],
    }));
    const descriptionByLang: Record<Lang, string> = {
      en: "Detached villa with private pool, outdoor kitchen and 4 bedrooms in La Nucia, Costa Blanca. Sleeps up to 8.",
      nl: "Vrijstaande villa met privézwembad, buitenkeuken en 4 slaapkamers in La Nucia, Costa Blanca. Geschikt voor tot 8 personen.",
      fr: "Villa indépendante avec piscine privée, cuisine extérieure et 4 chambres à La Nucia, Costa Blanca. Jusqu'à 8 personnes.",
      de: "Freistehende Villa mit privatem Pool, Außenküche und 4 Schlafzimmern in La Nucia, Costa Blanca. Für bis zu 8 Personen.",
    };
    const amenitiesByLang: Record<Lang, string[]> = {
      en: ["Private pool", "Outdoor kitchen", "Air conditioning", "Free Wi-Fi", "Private parking"],
      nl: ["Privézwembad", "Buitenkeuken", "Airconditioning", "Gratis wifi", "Privéparkeerplaats"],
      fr: ["Piscine privée", "Cuisine extérieure", "Climatisation", "Wi-Fi gratuit", "Parking privé"],
      de: ["Privater Pool", "Außenküche", "Klimaanlage", "Kostenloses WLAN", "Privatparkplatz"],
    };
    const touristTypeByLang: Record<Lang, string[]> = {
      en: ["Families", "Couples"],
      nl: ["Gezinnen", "Koppels"],
      fr: ["Familles", "Couples"],
      de: ["Familien", "Paare"],
    };
    const vacationRentalLd = {
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      name: "Casa Mi Pajobe",
      additionalType: "http://www.productontology.org/id/Villa",
      description: descriptionByLang[lang],
      url: `${SITE}/${lang}`,
      identifier: {
        "@type": "PropertyValue",
        name: "Tourist Rental License",
        value: "VT-454687-A",
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: "Urb. Panorama",
        addressLocality: "La Nucia",
        addressRegion: "Costa Blanca",
        postalCode: "03530",
        addressCountry: "ES",
      },
      geo: { "@type": "GeoCoordinates", latitude: 38.616, longitude: -0.1278 },
      image: SCHEMA_IMAGES,
      checkinTime: "16:00",
      checkoutTime: "10:00",
      priceRange: "€910 – €1795",
      currenciesAccepted: "EUR",
      telephone: "+32475921693",
      tourBookingPage: `${SITE}/book/${lang}`,
      touristType: touristTypeByLang[lang],
      containsPlace: {
        "@type": "Accommodation",
        name: "Casa Mi Pajobe Villa",
        numberOfRooms: 4,
        numberOfBedrooms: 4,
        occupancy: { "@type": "QuantitativeValue", maxValue: 8 },
        amenityFeature: amenitiesByLang[lang].map((name) => ({
          "@type": "LocationFeatureSpecification",
          name,
          value: true,
        })),
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: String(REVIEW_AGGREGATE.ratingValue),
        reviewCount: String(REVIEW_AGGREGATE.reviewCount),
        bestRating: "5",
        worstRating: "1",
      },
      review: reviews,
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: t.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    return {
      meta: [
        { title: TITLE_BY_LANG[lang] },
        { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
        { name: "description", content: META_DESC_BY_LANG[lang] },
        { property: "og:title", content: TITLE_BY_LANG[lang] },
        { property: "og:description", content: META_DESC_BY_LANG[lang] },
        { property: "og:url", content: `${SITE}/${lang}` },
        { property: "og:type", content: "website" },
        {
          property: "og:image",
          content: `${SITE}${HERO}`,
        },
        { property: "og:locale", content: lang },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE_BY_LANG[lang] },
        { name: "twitter:description", content: META_DESC_BY_LANG[lang] },
      ],
      links: [{ rel: "canonical", href: `${SITE}/${lang}` }, ...HREFLANG_LINKS],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(vacationRentalLd) },
        { type: "application/ld+json", children: JSON.stringify(faqLd) },
      ],
    };
  },
  component: LangPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-xl px-6 py-24 text-center">
      <h1 className="text-2xl font-semibold">Page not found</h1>
      <p className="mt-4 text-muted-foreground">
        That language isn't available. Please pick one from the homepage.
      </p>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-xl px-6 py-24 text-center">
      <h1 className="text-2xl font-semibold">Something went wrong</h1>
    </div>
  ),
});

function LangPage() {
  const { lang } = Route.useParams();
  if (!isLang(lang)) return null;
  return <VillaHome lang={lang} />;
}